import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { DesignSystemTheme } from '@minecraft.themes';
import { useResponsiveProps } from '../../hooks/responsiveProps';
import type { ResponsiveProps } from '../../hooks/responsiveProps.types';
import type { MediaPhotoSource } from './types';

const FALLBACK_SRC_SVG =
  'data:image/svg+xml,%3Csvg viewBox="0 0 16 9" xmlns="http://www.w3.org/2000/svg"%3E%3Crect width="16" height="9" fill="black"/%3E%3C/svg%3E';

type MediaPhotoResponsiveProps = {
  width?: number;
  height?: number;
  bgColor?: keyof DesignSystemTheme['bgColors'];
  objectFit?: React.CSSProperties['objectFit'];
};

export type MediaPhotoComponentProps = {
  /** URL to the media source */
  sources: MediaPhotoSource[];
  /** alt text for the image for accessibility */
  alt: string;
  /** Sets the `data-qa-id` attribute (Playwright selector). */
  'data-qa-id'?: string;
  /** Sets the `data-testid` attribute (Jest selector). */
  'data-testid'?: string;
  /** fixed pixel width that the image should be rendered at */
  width: number;
  /** fixed pixel height that the image should be rendered at */
  height: number;
  /** sets the background color of the video element if the video does not fill it completely */
  bgColor?: keyof DesignSystemTheme['bgColors'];
  /** fallback image to display if the main image fails to load */
  fallbackSrc?: string;
  /** CSS object-fit value to apply to the img element */
  objectFit?: React.CSSProperties['objectFit'];
} & Pick<ResponsiveProps<MediaPhotoResponsiveProps>, 'smProps' | 'mdProps' | 'lgProps'>;

/**
 * Finds the best image source from a list of sources based on the requested width and height.
 * Returns the URL of the selected source, or undefined if the source list is missing or empty.
 *
 * If it can find a source whose width and height are at least the requested dimensions, it uses
 * the one with the smallest pixel area.
 *
 * If no source meets both requested dimensions, it tries to find a source that at least meets the
 * width requirement, preferring the smallest pixel area among those sources.
 *
 * If no source meets the width requirement, it selects the widest available source, using height
 * to break ties.
 */
const findBestSource = (
  sources: MediaPhotoComponentProps['sources'],
  width: number,
  height: number
): string | undefined => {
  // Let the caller use its fallback when the source list is missing or empty.
  if (!Array.isArray(sources) || sources.length === 0) {
    return undefined;
  }

  // Higher tiers fit both dimensions, then width only, then neither.
  const getFitTier = (source: MediaPhotoSource) => {
    if (source.width >= width && source.height >= height) return 2;
    return source.width >= width ? 1 : 0;
  };

  // Keep the best candidate in one pass through the source list.
  const bestSource = sources.reduce<MediaPhotoSource | undefined>((best, source) => {
    if (!best) return source;

    const sourceFitTier = getFitTier(source);
    const bestFitTier = getFitTier(best);
    // A source that fits more of the requested dimensions takes priority.
    if (sourceFitTier !== bestFitTier) return sourceFitTier > bestFitTier ? source : best;

    // If all sources are too narrow, prefer the widest one.
    if (sourceFitTier === 0) {
      return source.width > best.width || (source.width === best.width && source.height > best.height) ? source : best;
    }

    // Among otherwise suitable sources, prefer the smallest image that fits.
    return source.width * source.height < best.width * best.height ? source : best;
  }, undefined);

  // An undefined result lets the component use its fallback image.
  return bestSource?.src;
};

/**
 * `MediaPhoto` — pure presentational `<img>` element driven by a URL.
 *
 * Caller is responsible for any wrapping affordances (download buttons,
 * lock banners, etc.).
 *
 * Note: the images your provide in the sources should match the aspect ratio
 * of the `width` and `height` props to avoid unexpected cropping, or overflow issues.
 *
 * If you have dyanmic photo sources that do not have a consistent aspect ratio
 * then you will have to deal with potential overflow issues if using objectFit="contain".
 * or potential cropping issues if using objectFit="cover". Most likely in those cases you
 * will want to actually use MediaPhotoFluid.
 *
 * By default this uses objectFit="contain" to ensure the image fits within the specified dimensions
 * and maintain its aspect ratio within the given width and height.
 *
 * You can also use objectFit="cover" if you want the image to fill the specified dimensions even
 * if it means cropping parts of the image that expand beyond the specified width and height.
 */
export const MediaPhotoFixed = ({
  alt,
  sources,
  'data-qa-id': qaId,
  'data-testid': testId,
  width,
  height,
  bgColor,
  fallbackSrc,
  objectFit = 'contain',
  smProps,
  mdProps,
  lgProps,
}: MediaPhotoComponentProps) => {
  const [failedSrcs, setFailedSrcs] = useState<Set<string>>(() => new Set());
  const theme = useTheme();
  const responsiveProps = useResponsiveProps({
    props: { width, height, bgColor, objectFit },
    smProps,
    mdProps,
    lgProps,
  });
  const {
    width: responsiveWidth,
    height: responsiveHeight,
    bgColor: responsiveBgColor,
    objectFit: responsiveObjectFit,
  } = responsiveProps;
  const errorSrc = useMemo(() => fallbackSrc ?? FALLBACK_SRC_SVG, [fallbackSrc]);
  const selectedSrc = useMemo(
    () =>
      findBestSource(
        Array.isArray(sources) ? sources.filter((source) => !failedSrcs.has(source.src)) : [],
        responsiveWidth,
        responsiveHeight
      ),
    [sources, responsiveWidth, responsiveHeight, failedSrcs]
  );
  const src = selectedSrc ?? errorSrc;
  const hasValidDimensions =
    Number.isFinite(responsiveWidth) &&
    responsiveWidth > 0 &&
    Number.isFinite(responsiveHeight) &&
    responsiveHeight > 0;

  if (!hasValidDimensions) return null;

  return (
    <img
      alt={alt}
      src={src}
      style={{
        backgroundColor: theme.designSystem.bgColors[responsiveBgColor ?? 'card'],
        objectFit: responsiveObjectFit,
        maxWidth: '100%',
        width: responsiveWidth,
        maxHeight: responsiveHeight,
        height: 'auto',
      }}
      {...(qaId ? { 'data-qa-id': qaId } : {})}
      {...(testId ? { 'data-testid': testId } : {})}
      onError={() => {
        if (!selectedSrc) return;
        setFailedSrcs((currentFailedSrcs) => {
          if (currentFailedSrcs.has(selectedSrc)) return currentFailedSrcs;
          return new Set(currentFailedSrcs).add(selectedSrc);
        });
      }}
    />
  );
};

MediaPhotoFixed.displayName = 'MediaPhotoFixed';
